import React, {useCallback, useMemo} from 'react'
import {useForm} from 'react-hook-form'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {AuthFormSimpleRegisterData, ObjectState} from 'types'
import {Button, Input, Modal, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'

const StyledTitle = styled(Paragraph)`
  text-align: center;
  margin-bottom: ${convertUnit(16)};
`
const StyledModal = styled(Modal)`
  width: 100%;
  max-width: ${convertUnit(335)};
  padding: ${convertUnit(20)};
`
const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: ${convertUnit(20)};
  gap: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  width: 100%;
`

interface GiftShopTemplateModalConfirmPasswordProps {
  stateModal: ObjectState<boolean>
  onCancel?(): void
  onConfirm(password: string): void
  stateLoading: ObjectState<boolean>
  stateError: ObjectState<string>
}

export default function GiftShopTemplateModalConfirmPassword({
  stateModal,
  onCancel,
  onConfirm,
  stateError,
  stateLoading,
}: GiftShopTemplateModalConfirmPasswordProps) {
  const {translate} = useTranslation()
  const [modalPassword, setModalPassword] = stateModal
  const form = useForm<AuthFormSimpleRegisterData>({
    defaultValues: {
      password: '',
    },
  })
  const {watch, errors} = form
  const {password} = watch()
  const loading = stateLoading[0]
  const [passwordError, setPasswordError] = stateError

  const handleCancel = useCallback(() => {
    setModalPassword(false)
    setPasswordError('')
    onCancel && onCancel()
  }, [onCancel, setModalPassword, setPasswordError])

  const isDisabled = useMemo(
    () => password === '' || errors.password !== undefined,
    [errors.password, password],
  )

  const handleRenderError = useMemo(
    () =>
      passwordError ? (
        <Paragraph fontWeight="medium" color="danger_5">
          {passwordError}
        </Paragraph>
      ) : (
        <></>
      ),
    [passwordError],
  )

  return (
    <StyledModal visible={modalPassword} onBackdropPress={handleCancel}>
      <StyledTitle fontSize="l" fontWeight="bold">
        {translate('auth:createPasswordSSOConfirmationPlaceholder')}
      </StyledTitle>
      <Input
        label={translate('global:password')}
        form={form}
        name="password"
        type="password"
        onChangeText={() => {
          setPasswordError('')
        }}
        rightIcon="visibility"
        autoCapitalize="none"
        placeholder={translate('auth:createPasswordSSOConfirmationPlaceholder')}
      />
      {handleRenderError}
      <StyledButtonContainer>
        <StyledButton
          label={translate('global:cancel')}
          color="primary_5"
          backgroundColor="white_3"
          onClick={handleCancel}
        />
        <StyledButton
          label={translate('global:confirm')}
          disabled={isDisabled}
          isLoading={loading}
          onClick={() => onConfirm(password)}
        />
      </StyledButtonContainer>
    </StyledModal>
  )
}
